// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Icon from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Tooltip, { TooltipPresentationProps } from "@keller/MobiusOverrides/Tooltip";
import { styledLinksMenu } from "@keller/XDL/Utilities";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface PaginationTooltipProps {
    currentPage: number;
    finalPageIndex: number;
    maxNoOfPagination: number;
    tooltipRef: React.MutableRefObject<any>;
    onChangePage: (page: number) => void;
    setIsMoveToModalOpen: (value: boolean) => void;
}

export interface PaginationTooltipStyles {
    tooltip?: TooltipPresentationProps;
    tooltipTriggerComponent?: { wrapper?: InjectableCss; label?: TypographyPresentationProps };
}

const paginationTooltipStyles: PaginationTooltipStyles = {
    tooltip: {
        cssOverrides: {
            tooltipWrapper: css`
                line-height: 0;
                display: flex;
            `,
            tooltipContainer: css`
                ${styledLinksMenu(180)}
            `,
        },
        extendedStyles: {
            tooltipContent: {
                css: css`
                    padding: 0;
                    min-width: 180px;
                `,
            },
        },
    },
    tooltipTriggerComponent: {
        wrapper: {
            css: css`
                display: flex;
                align-items: center;
            `,
        },
        label: {
            size: 15,
            css: css`
                letter-spacing: 0;
                line-height: 24px;
                margin-right: 8px;
            `,
        },
    },
};

const styles = paginationTooltipStyles;
const PaginationTooltip: FC<PaginationTooltipProps> = ({
    currentPage,
    finalPageIndex,
    maxNoOfPagination,
    tooltipRef,
    onChangePage,
    setIsMoveToModalOpen,
}) => {
    // XNG-Changes     
    const [isOpen, setIsOpen] = useState(false);
    const generatePagesList = () => {
        const pagesList = [];
        if (finalPageIndex > maxNoOfPagination) {
            pagesList.push(
                <Link
                    key="page_ChangeByModal"
                    onClick={() => {
                        setIsMoveToModalOpen(true);
                        // XNG-Changes 
                        setIsOpen(false);
                    }}
                >
                    <Typography>{translate("Enter Your Page #")}</Typography>
                </Link>,
            );
        }

        const initialPageNumber = currentPage - maxNoOfPagination > 0 ? currentPage - maxNoOfPagination : 1;
        const finalPageNumber =
            currentPage + maxNoOfPagination < finalPageIndex ? currentPage + maxNoOfPagination : finalPageIndex;

        for (let pageNumber = initialPageNumber; pageNumber <= finalPageNumber; pageNumber++) {
            if (pageNumber === currentPage) {
                continue;
            }

            pagesList.push(
                <Link
                    key={`page_${pageNumber}`}
                    onClick={() => {
                        onChangePage(pageNumber);
                        // XNG-Changes 
                        setIsOpen(false);
                    }}
                >
                    <Typography>
                        {translate("Page")} {pageNumber}
                    </Typography>
                </Link>,
            );
        }

        return pagesList;
    };

    const pagesList = generatePagesList();
    return (
        <li>
            <Tooltip
                text={pagesList}
                triggerComponent={
                    <StyledWrapper {...styles.tooltipTriggerComponent?.wrapper}>
                        <Typography
                            className="paginationTooltip"
                            {...styles.tooltipTriggerComponent?.label}
                        >{`${translate("Page")} ${currentPage} ${translate("of")} ${finalPageIndex}`}</Typography>
                        <Icon src={ChevronDown} size={12} color="common.ui7" />
                    </StyledWrapper>
                }
                offset={{ mainAxis: 20 }}
                // XNG-Changes 
                isOpen={isOpen}
                onOpenChange={(open: boolean) => setIsOpen(open)}
                {...styles.tooltip}
            />
        </li>
    );
};

export default PaginationTooltip;
